import { useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  Users,
  X,
} from "lucide-react";
import AccessItemPicker from "./AccessItemPicker";

const emptyForm = () => ({
  recipientType: "group",
  groupId: "",
  email: "",
  userId: "",
  accessScope: "selected",
  selectedIds: [],
  expiresAt: "",
});

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const text = String(value).trim().replace(" ", "T");
  const iso = /(?:Z|[+-]\d\d:?\d\d)$/i.test(text) ? text : `${text}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toLocalInput = (value) => {
  const date = toDate(value);
  if (!date) return "";
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const formatExpiry = (value) =>
  toDate(value)?.toLocaleString() || "Never expires";

export function CreatorAccessSection({ resource, config }) {
  const {
    groups,
    items,
    grants,
    loading,
    refreshing,
    saving,
    revokingId,
    error,
    notice,
    refresh,
    saveGrant,
    revokeGrant,
    clearFeedback,
  } = resource;

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("all");
  const editorRef = useRef(null);

  const busy = loading || refreshing || saving || revokingId != null;

  const groupGrantMap = useMemo(() => {
    const map = new Map();
    for (const grant of grants) {
      if (grant.recipientType === "group" && grant.groupId != null) {
        map.set(String(grant.groupId), grant);
      }
    }
    return map;
  }, [grants]);

  const duplicateGroup =
    !editingId && form.recipientType === "group" && form.groupId
      ? groupGrantMap.get(String(form.groupId))
      : null;

  const filteredGrants = useMemo(() => {
    const term = search.trim().toLowerCase();
    return grants.filter((grant) => {
      const label =
        grant.recipientType === "group"
          ? grant.groupName || grant.groupId
          : grant.userEmail || grant.userId;
      return (
        (recipientFilter === "all" ||
          recipientFilter === grant.recipientType) &&
        (!term ||
          String(label || "")
            .toLowerCase()
            .includes(term))
      );
    });
  }, [grants, search, recipientFilter]);

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setFormError("");
  };

  const openEditor = (grant = null) => {
    clearFeedback();
    setFormError("");
    setEditingId(grant ? String(grant.grantId) : null);
    setForm(
      grant
        ? {
            recipientType: grant.recipientType,
            groupId: grant.groupId == null ? "" : String(grant.groupId),
            email: grant.userEmail || "",
            userId: grant.userId == null ? "" : String(grant.userId),
            accessScope: grant.accessScope,
            selectedIds: (grant[config.grantIdsKey] || []).map(Number),
            expiresAt: toLocalInput(grant.expiresAt),
          }
        : emptyForm(),
    );
    setEditorOpen(true);
    requestAnimationFrame(() =>
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingId(null);
    setPickerOpen(false);
    setForm(emptyForm());
    setFormError("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    clearFeedback();
    setFormError("");

    if (form.recipientType === "group" && !form.groupId) {
      return setFormError("Choose a creator group.");
    }
    if (duplicateGroup) {
      return setFormError(
        "This group already has a grant. Edit that grant instead.",
      );
    }
    if (form.recipientType === "user" && !form.userId && !form.email.trim()) {
      return setFormError("Enter a registered Katch user's email.");
    }
    if (form.accessScope === "selected" && form.selectedIds.length === 0) {
      return setFormError(`Choose at least one ${config.itemSingular}.`);
    }
    if (form.selectedIds.length > 100) {
      return setFormError("Select no more than 100 items per grant.");
    }

    let expiresAt = null;
    if (form.expiresAt) {
      const date = new Date(form.expiresAt);
      if (Number.isNaN(date.getTime()) || date.getTime() <= Date.now()) {
        return setFormError("Choose a future expiration date and time.");
      }
      expiresAt = date.toISOString();
    }

    const success = await saveGrant({ ...form, expiresAt }, editingId);
    if (success) closeEditor();
  };

  const confirmRevoke = async (grant) => {
    if (
      !window.confirm(
        `Revoke ${config.label.toLowerCase()} access for this recipient?`,
      )
    )
      return;
    const success = await revokeGrant(grant.grantId);
    if (success && editingId === String(grant.grantId)) closeEditor();
  };

  return (
    <section className="cfa-section">
      <div className="cfa-section__heading">
        <div>
          <h2>{config.label} access</h2>
          <p>
            Grant selected Katch users or approved groups access to{" "}
            {config.label.toLowerCase()}.
          </p>
        </div>
        <div className="cfa-actions">
          <button
            type="button"
            className="cfa-btn cfa-btn--muted"
            disabled={busy}
            onClick={() => refresh()}
          >
            <RefreshCw size={16} className={refreshing ? "cfa-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            className="cfa-btn cfa-btn--primary"
            disabled={busy}
            onClick={() => (editorOpen ? closeEditor() : openEditor())}
          >
            {editorOpen ? <X size={16} /> : <Plus size={16} />}
            {editorOpen ? "Close editor" : "Grant access"}
          </button>
        </div>
      </div>

      {error && (
        <div className="cfa-alert cfa-alert--error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="cfa-alert cfa-alert--success" role="status">
          {notice}
        </div>
      )}

      <div className="cfa-stats">
        <div>
          <ShieldCheck size={17} />
          <strong>{grants.length}</strong>
          <span>Total grants</span>
        </div>
        <div>
          <CheckCircle2 size={17} />
          <strong>{grants.filter((g) => g.status === "active").length}</strong>
          <span>Active</span>
        </div>
        <div>
          <Clock3 size={17} />
          <strong>{grants.filter((g) => g.status === "expired").length}</strong>
          <span>Expired</span>
        </div>
      </div>

      {editorOpen && (
        <form
          className="cfa-panel cfa-editor"
          ref={editorRef}
          onSubmit={submit}
        >
          <div className="cfa-panel__heading">
            <div>
              <h3>
                {editingId
                  ? "Edit access"
                  : `Grant ${config.label.toLowerCase()} access`}
              </h3>
              <p>
                {editingId
                  ? "Update existing permissions."
                  : "Choose a recipient and what they can view."}
              </p>
            </div>
            <button
              className="cfa-icon"
              type="button"
              onClick={closeEditor}
              disabled={saving}
              aria-label="Close editor"
            >
              <X size={18} />
            </button>
          </div>

          <fieldset
            className="cfa-fieldset"
            disabled={Boolean(editingId) || busy}
          >
            <legend>Recipient</legend>
            <div className="cfa-choice-row">
              <label
                className={form.recipientType === "group" ? "is-selected" : ""}
              >
                <input
                  type="radio"
                  name="recipientType"
                  checked={form.recipientType === "group"}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      recipientType: "group",
                      userId: "",
                      email: "",
                    }))
                  }
                />
                <Users size={16} /> Group
              </label>
              <label
                className={form.recipientType === "user" ? "is-selected" : ""}
              >
                <input
                  type="radio"
                  name="recipientType"
                  checked={form.recipientType === "user"}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      recipientType: "user",
                      groupId: "",
                    }))
                  }
                />
                <UserRound size={16} /> Individual
              </label>
            </div>
          </fieldset>

          <div className="cfa-field">
            {form.recipientType === "group" ? (
              <>
                <label htmlFor="cfa-recipient-group">Creator group</label>
                <select
                  id="cfa-recipient-group"
                  value={form.groupId}
                  required
                  disabled={Boolean(editingId) || busy}
                  onChange={(e) => setField("groupId", e.target.value)}
                >
                  <option value="">Select a group</option>
                  {groups.map((group) => {
                    const priorGrant = groupGrantMap.get(String(group.groupId));
                    return (
                      <option
                        value={group.groupId}
                        key={group.groupId}
                        disabled={!editingId && Boolean(priorGrant)}
                      >
                        {group.groupName}
                        {priorGrant ? ` — ${priorGrant.status}` : ""}
                      </option>
                    );
                  })}
                </select>
                {!loading && groups.length === 0 && (
                  <small>No creator groups available.</small>
                )}
                {duplicateGroup && (
                  <small>
                    This group has a grant already; edit the existing one
                    instead.
                  </small>
                )}
              </>
            ) : (
              <>
                <label htmlFor="cfa-recipient-email">
                  Registered user email
                </label>
                <input
                  id="cfa-recipient-email"
                  type="email"
                  value={form.email}
                  placeholder="player@example.com"
                  required={!editingId}
                  disabled={Boolean(editingId) || busy}
                  onChange={(e) => setField("email", e.target.value)}
                />
                <small>Only registered Katch users can receive access.</small>
              </>
            )}
          </div>

          <fieldset className="cfa-fieldset" disabled={busy}>
            <legend>Access scope</legend>
            <div className="cfa-choice-row">
              <label
                className={form.accessScope === "all" ? "is-selected" : ""}
              >
                <input
                  type="radio"
                  name="accessScope"
                  checked={form.accessScope === "all"}
                  onChange={() => setField("accessScope", "all")}
                />
                All {config.label.toLowerCase()}
              </label>
              <label
                className={form.accessScope === "selected" ? "is-selected" : ""}
              >
                <input
                  type="radio"
                  name="accessScope"
                  checked={form.accessScope === "selected"}
                  onChange={() => setField("accessScope", "selected")}
                />
                Selected {config.label.toLowerCase()}
              </label>
            </div>
            <small>
              All access covers current and future eligible content.
            </small>
          </fieldset>

          {form.accessScope === "selected" && (
            <div className="cfa-selection">
              <div>
                <strong>Selected {config.label.toLowerCase()}</strong>
                <small>{form.selectedIds.length} of 100 selected</small>
              </div>
              <button
                type="button"
                className="cfa-btn cfa-btn--muted"
                disabled={busy}
                onClick={() => setPickerOpen(true)}
              >
                {form.selectedIds.length
                  ? "Manage selection"
                  : `Choose ${config.label.toLowerCase()}`}
              </button>
            </div>
          )}

          <div className="cfa-field">
            <label htmlFor="cfa-expiry">Expires (optional)</label>
            <input
              id="cfa-expiry"
              type="datetime-local"
              value={form.expiresAt}
              disabled={busy}
              onChange={(e) => setField("expiresAt", e.target.value)}
            />
            <small>Leave blank if the grant should never expire.</small>
          </div>

          {formError && (
            <div role="alert" className="cfa-alert cfa-alert--error">
              {formError}
            </div>
          )}
          <div className="cfa-actions">
            <button
              type="submit"
              className="cfa-btn cfa-btn--primary"
              disabled={busy || Boolean(duplicateGroup)}
            >
              {saving
                ? "Saving..."
                : editingId
                  ? "Save changes"
                  : "Grant access"}
            </button>
            <button
              type="button"
              className="cfa-btn cfa-btn--muted"
              disabled={saving}
              onClick={closeEditor}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="cfa-panel">
        <div className="cfa-panel__heading">
          <div>
            <h3>Existing grants</h3>
            <p>Manage access without opening the editor.</p>
          </div>
        </div>
        <div className="cfa-tools">
          <div className="cfa-search">
            <Search size={16} />
            <input
              type="search"
              placeholder="Search users or groups"
              aria-label="Search grants"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={recipientFilter}
            onChange={(e) => setRecipientFilter(e.target.value)}
            aria-label="Filter recipients"
          >
            <option value="all">All recipients</option>
            <option value="group">Groups</option>
            <option value="user">Individuals</option>
          </select>
        </div>

        {loading ? (
          <div className="cfa-empty">Loading grants...</div>
        ) : filteredGrants.length === 0 ? (
          <div className="cfa-empty">No access grants found.</div>
        ) : (
          <div className="cfa-grants">
            {filteredGrants.map((grant) => {
              const count = (grant[config.grantIdsKey] || []).length;
              const label =
                grant.recipientType === "group"
                  ? grant.groupName || `Group #${grant.groupId}`
                  : grant.userEmail || `User #${grant.userId}`;

              return (
                <article className="cfa-grant" key={grant.grantId}>
                  <div className="cfa-grant__main">
                    <span className="cfa-avatar">
                      {grant.recipientType === "group" ? (
                        <Users size={18} />
                      ) : (
                        <UserRound size={18} />
                      )}
                    </span>
                    <div className="cfa-grant__body">
                      <div className="cfa-grant__title">
                        <strong>{label}</strong>
                        <span
                          className={`cfa-status cfa-status--${grant.status || "unknown"}`}
                        >
                          {grant.status || "unknown"}
                        </span>
                      </div>
                      <small>
                        {grant.accessScope === "all"
                          ? `All eligible ${config.label.toLowerCase()}`
                          : `${count} selected ${config.label.toLowerCase()}`}
                      </small>
                      <small>{formatExpiry(grant.expiresAt)}</small>
                    </div>
                  </div>
                  <div className="cfa-grant__actions">
                    <button
                      type="button"
                      className="cfa-btn cfa-btn--muted"
                      disabled={busy}
                      onClick={() => openEditor(grant)}
                    >
                      <Pencil size={14} />
                      {grant.status === "revoked" ? "Reactivate" : "Edit"}
                    </button>
                    {grant.status !== "revoked" && (
                      <button
                        type="button"
                        className="cfa-btn cfa-btn--danger"
                        disabled={busy}
                        onClick={() => confirmRevoke(grant)}
                      >
                        <Trash2 size={14} />
                        {String(revokingId) === String(grant.grantId)
                          ? "Revoking..."
                          : "Revoke"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <AccessItemPicker
        open={pickerOpen && editorOpen && form.accessScope === "selected"}
        title={config.label}
        items={items}
        itemIdKey={config.itemIdKey}
        selectedIds={form.selectedIds}
        disabled={busy}
        onChange={(ids) => setField("selectedIds", ids)}
        onClose={() => setPickerOpen(false)}
      />
    </section>
  );
}

export default CreatorAccessSection;
